import { TDevice } from '@cian/utils';

import { getMapTelemetryConfig } from './config';
import { EClustersFailReason, EPinsUpdateTrigger, TELEMETRY_KEYS, UNKNOWN_DIMENSION } from './constants';
import {
  clearPendingPinsUpdate,
  getPendingPinsUpdate,
  markPendingPinsUpdateDataReady,
  startPendingPinsUpdate,
} from './pinsUpdateState';
import { getPinsCountBucket, getRoute, resolveFailReason } from './utils';

import type { IConfig } from '@cian/config/shared';
import type { ILogger } from '@cian/logger/shared';
import type { ITelemetryLogger } from '@cian/telemetry/shared';

export interface IMapTelemetryDependencies {
  telemetry: ITelemetryLogger | null;
  deviceType?: TDevice;
  /** Нужен только для ошибок отрисовки: метрика говорит сколько, лог — что именно сломалось */
  logger?: ILogger;
  /** Источник рубильника и параметров сбора. Без него действуют значения по умолчанию */
  config?: IConfig;
}

/**
 * Сквозное время загрузки карты отправляется один раз за страницу.
 * Флаг держим на уровне модуля, а не экземпляра фасада: источником сигнала служит слой пинов,
 * и при его пересоздании в метрику попал бы второй, заведомо неверный замер.
 */
let isMapLoadReported = false;

/** Только для тестов: сбрасывает признак уже отправленного замера загрузки */
export function resetMapLoadReported() {
  isMapLoadReported = false;
}

export interface IMapTelemetry {
  /** Карта проинициализирована и готова к взаимодействию */
  trackMapInitialized(): void;
  /** Пины перерисованы. Закрывает замеры загрузки карты и обновления пинов, если они открыты */
  trackPinsRendered(): void;
  /** Пользователь закончил взаимодействие с картой — открывает замер SLI 2 */
  startPinsUpdate(trigger: EPinsUpdateTrigger): void;
  /** Запрос кластеров ушёл на бэкенд: фиксирует, сколько до этого занял наш троттлинг */
  trackPinsUpdateRequestStarted(): void;
  /** Ручка кластеров ответила — независимо от того, успехом или отказом */
  trackClustersRequestDuration(durationMs: number): void;
  /** Кластеры загружены и отданы в стор */
  trackClustersSucceed(): void;
  /** Запрос кластеров завершился отказом. Причина определяется по типу ошибки */
  trackClustersFailed(error: unknown): void;
  /** Запрос отменён — пользователь продолжил двигать карту. В success/fail не попадает */
  trackClustersCancelled(): void;
  /** Сделана повторная попытка запроса */
  trackClustersRetried(): void;
  /** SLI 6: тайл отрисован. Замер сэмплируется, пустые тайлы не считаются */
  trackTileRendered(pinsCount: number, durationMs: number): void;
  /** SLI 7: часть пинов в тайле не отрисовалась */
  trackPinRenderErrors(errorsCount: number, error: unknown): void;
  /** SLI 8 и 9: сколько пинов пришло от бэкенда и сколько из них пригодны к отрисовке */
  trackPinsData(deliveredCount: number, preparedCount: number): void;
}

export const NOOP_MAP_TELEMETRY: IMapTelemetry = {
  trackMapInitialized: () => undefined,
  trackPinsRendered: () => undefined,
  startPinsUpdate: () => undefined,
  trackPinsUpdateRequestStarted: () => undefined,
  trackClustersRequestDuration: () => undefined,
  trackClustersSucceed: () => undefined,
  trackClustersFailed: () => undefined,
  trackClustersCancelled: () => undefined,
  trackClustersRetried: () => undefined,
  trackTileRendered: () => undefined,
  trackPinRenderErrors: () => undefined,
  trackPinsData: () => undefined,
};

/**
 * Единая точка отправки технических метрик карты.
 *
 * Собран фасадом, а не разрозненными вызовами increment, по трём причинам:
 * ключи лежат в одном реестре и не расползаются, разрезы дописываются одинаково во всех метриках,
 * и вызов безопасен когда телеметрии нет — на сервере и в тестах.
 */
export function createMapTelemetry({
  telemetry,
  deviceType,
  logger,
  config,
}: IMapTelemetryDependencies): IMapTelemetry {
  const { enabled, renderSamplingRate } = getMapTelemetryConfig(config);

  /**
   * Рубильник из Runtime Settings. Возвращаем пустую реализацию, а не проверяем флаг
   * в каждом методе: так выключение гарантированно доходит до всех точек вызова.
   */
  if (!enabled) {
    return NOOP_MAP_TELEMETRY;
  }

  const dimensions = `${deviceType || UNKNOWN_DIMENSION}.${getRoute()}`;

  const increment = (key: string, value?: number) => {
    const fullKey = `${key}.${dimensions}`;

    /** Не передаём undefined в пакет телеметрии: у increment своё значение по умолчанию */
    if (value === undefined) {
      telemetry?.increment(fullKey);

      return;
    }

    telemetry?.increment(fullKey, value);
  };

  /** statsd работает с целыми миллисекундами, дробная часть performance.now() ему не нужна */
  const timing = (key: string, durationMs: number) => {
    telemetry?.timing(`${key}.${dimensions}`, Math.round(durationMs));
  };

  return {
    trackMapInitialized: () => timing(TELEMETRY_KEYS.mapInit, performance.now()),

    trackPinsRendered: () => {
      if (!isMapLoadReported) {
        isMapLoadReported = true;

        /** performance.now() отсчитывается от начала навигации, это сразу искомая длительность */
        timing(TELEMETRY_KEYS.mapLoad, performance.now());
      }

      const pendingUpdate = getPendingPinsUpdate();

      if (!pendingUpdate?.isDataReady) {
        return;
      }

      timing(`${TELEMETRY_KEYS.pinsUpdate}.${pendingUpdate.trigger}`, performance.now() - pendingUpdate.startedAt);
      clearPendingPinsUpdate();
    },

    startPinsUpdate: (trigger: EPinsUpdateTrigger) => startPendingPinsUpdate(trigger, performance.now()),

    trackPinsUpdateRequestStarted: () => {
      const pendingUpdate = getPendingPinsUpdate();

      if (!pendingUpdate) {
        return;
      }

      timing(`${TELEMETRY_KEYS.pinsUpdateDelay}.${pendingUpdate.trigger}`, performance.now() - pendingUpdate.startedAt);
    },

    trackClustersRequestDuration: (durationMs: number) => timing(TELEMETRY_KEYS.clustersRequest, durationMs),

    trackClustersSucceed: () => {
      markPendingPinsUpdateDataReady();
      increment(TELEMETRY_KEYS.clustersSucceed);
    },

    trackClustersFailed: (error: unknown) => {
      const reason: EClustersFailReason = resolveFailReason(error);

      /**
       * Шлём и агрегат, и сегментированную метрику: по агрегату считается SLO,
       * по сегментам разбираются инциденты.
       */
      increment(TELEMETRY_KEYS.clustersFail);
      increment(`${TELEMETRY_KEYS.clustersFailReason}.${reason}`);

      /** Пины не обновятся — замер SLI 2 закрывать нечем, иначе он достанется следующей перерисовке */
      clearPendingPinsUpdate();
    },

    trackClustersCancelled: () => increment(TELEMETRY_KEYS.clustersCancelled),

    trackClustersRetried: () => increment(TELEMETRY_KEYS.clustersRetry),

    trackTileRendered: (pinsCount: number, durationMs: number) => {
      /** Пустой тайл рисуется мгновенно и только занизил бы перцентили */
      if (pinsCount === 0 || Math.random() >= renderSamplingRate) {
        return;
      }

      timing(`${TELEMETRY_KEYS.pinsTileRender}.${getPinsCountBucket(pinsCount)}`, durationMs);
    },

    trackPinRenderErrors: (errorsCount: number, error: unknown) => {
      if (errorsCount === 0) {
        return;
      }

      /**
       * Один вызов на тайл, но со счётчиком всех упавших в нём пинов:
       * инкремент на каждый пин залил бы приёмник, а терять количество не хочется.
       */
      increment(TELEMETRY_KEYS.pinsRenderError, errorsCount);
      /** В catch может прилететь что угодно, а логгер принимает только строку или Error */
      logger?.error(error instanceof Error ? error : String(error), {
        domain: 'shared/map-search/utils/ymapsModules/PointsLayer#drawTile()',
      });
    },

    trackPinsData: (deliveredCount: number, preparedCount: number) => {
      if (deliveredCount === 0) {
        return;
      }

      /**
       * Два счётчика вместо одной разницы: по ним в Grafana видно и объём выдачи,
       * и долю потерь. Одна разница без знаменателя ничего не говорит.
       */
      increment(TELEMETRY_KEYS.pinsDelivered, deliveredCount);
      increment(TELEMETRY_KEYS.pinsPrepared, preparedCount);

      const invalidCount = deliveredCount - preparedCount;

      /** В норме счётчик пустой, поэтому любое ненулевое значение — сигнал расхождения с контрактом */
      if (invalidCount > 0) {
        increment(TELEMETRY_KEYS.pinsDataInvalid, invalidCount);
      }
    },
  };
}
